// OKTA NOTE: This file is only used in Dev / Staging post Okta.
// TLDR: Prioritize work on it accordingly, if it's not in Prod, you have more pressing matters.
import React, { useEffect, useState } from "react";
import { Redirect } from "react-router";
import { Logo } from "@sonder/ui/source/icons";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { useSegment } from "analytics/segment";
import { ForgotPasswordLink } from "auth/components/ForgotPasswordLink";
import { LoginErrorMessage } from "auth/components/LoginErrorMessage";
import { LoginField } from "auth/components/LoginField";
import { LoginForm } from "auth/components/LoginForm";
import { LoginViewContainer } from "auth/components/LoginViewContainer";
import { LoginViewHeader } from "auth/components/LoginViewHeader";
import { useLoggedInUser } from "auth/hooks";
import { AUTH_TOKEN_KEY } from "core/constants";

// Helpers
const onLogin = async (email: string, password: string) => {
  const res = await fetch(`${ADMIN_HOST}/api/users/token`, {
    headers: {
      "Content-Type": "application/json",
      SONDER_APP: "dispatch-dashboard",
    },
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();

  // Handle errors
  if (json.errors.length > 0) throw new Error(json.errors[0]);

  if (typeof json.token !== "string")
    throw new Error(
      `Invalid auth token. Expected type 'string', got type ${typeof json.token}`,
    );

  // Token is valid, write it to storage
  localStorage.setItem(AUTH_TOKEN_KEY, json.token);
  return json.token;
};

// Exports
export const LoginView = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");
  const { track } = useSegment();

  const { user, error: gqlError, loading, token, setToken } = useLoggedInUser();

  // We don't want HK contractors to use the dashboard
  const isHKContractor = user?.isHKContractor;
  const isHKManager = user?.isHKManager;
  useEffect(() => {
    if (isHKManager) return;

    if (!loading && isHKContractor) {
      setLoginError("HK contractors do not have access to the dashboard");
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setToken(null);
    }
  }, [isHKContractor, isHKManager, loading]);

  if ((!isHKContractor || isHKManager) && user && user.id && token !== null) {
    const originalPath = localStorage.getItem("originalPath");
    localStorage.removeItem("originalPath");
    return <Redirect to={originalPath || "/tasks"} />;
  }

  const disabled = submitting || loading;
  const loginErrorMessage = gqlError || loginError;
  return (
    <LoginViewContainer>
      <LoginViewHeader>
        <Logo />
      </LoginViewHeader>
      <LoginForm>
        <LoginField
          disabled={disabled}
          error={Boolean(loginErrorMessage)}
          label="Email"
          name="email"
          type="email"
          onChange={e => setEmail(e.target.value)}
        />
        <LoginField
          disabled={disabled}
          error={Boolean(loginErrorMessage)}
          label="Password"
          name="password"
          type="password"
          onChange={e => setPassword(e.target.value)}
        />
        <LoginErrorMessage error={Boolean(loginErrorMessage)}>
          {loginErrorMessage}
        </LoginErrorMessage>
        <Button
          data-cy="LogInButton"
          disabled={disabled}
          onClick={event => {
            event.preventDefault();
            track({
              eventName: "login_view",
              action: "click",
              element: "sign_in_button",
            });
            setLoginError("");
            setIsSubmitting(true);
            onLogin(email, password)
              .then(authToken => {
                setIsSubmitting(false);
                setToken(authToken);
                track({
                  eventName: "login_view",
                  action: "login_success",
                });
              })
              .catch(e => {
                setIsSubmitting(false);
                setLoginError(e.message);
                track({
                  eventName: "login_view",
                  action: "login_failure",
                });
              });
          }}
        >
          {disabled ? "Signing in..." : "Sign in"}
        </Button>
      </LoginForm>
      <ForgotPasswordLink href={`${ADMIN_HOST}/users/password/new`}>
        Forgot your password?
      </ForgotPasswordLink>
    </LoginViewContainer>
  );
};
